import React, { useState, useEffect, useRef } from 'react';
import { fabric } from 'fabric';
import useEditorStore from '../store/useEditorStore';
import { applyStrokeSettings } from '../utils/strokeUtils';

interface ShapePickerProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

interface ShapeDefinition {
  id: string;
  label: string;
  color: string;
  icon: React.ReactNode;
  createShape: (canvas: fabric.Canvas, shapeSettings: any, color: string) => fabric.Object;
}

const commonProps = (shapeSettings: any, defaultColor?: string) => ({
  fill: shapeSettings.fillEnabled ? (defaultColor || shapeSettings.fillColor) : 'transparent',
  stroke: shapeSettings.strokeEnabled ? shapeSettings.strokeColor : '',
  strokeWidth: shapeSettings.strokeEnabled ? shapeSettings.strokeWidth : 0,
  selectable: true,
  hasControls: true,
  hasBorders: true,
  cornerStyle: 'circle' as const,
  cornerSize: 8,
  transparentCorners: false,
  cornerColor: '#4285f4',
  cornerStrokeColor: '#ffffff',
  borderColor: '#4285f4',
  borderScaleFactor: 1,
  padding: 8,
});

const maybeApplyStroke = (obj: fabric.Object, shapeSettings: any) => {
  if (shapeSettings.strokeEnabled && shapeSettings.strokeColor && shapeSettings.strokeWidth > 0) {
    applyStrokeSettings(obj, shapeSettings.strokeColor, shapeSettings.strokeWidth);
  }
};

const ShapePicker: React.FC<ShapePickerProps> = ({ isOpen, onClose, triggerRef }) => {
  const { canvas, shapeSettings, setSelectedTool, setSelectedObject, addToHistory } = useEditorStore();
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);

  // Helper: create a polygon with points centered at origin, then position at canvas center
  const createCenteredPolygon = (canvas: fabric.Canvas, shapeSettings: any, color: string, pts: { x: number; y: number }[]): fabric.Polygon => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    const poly = new fabric.Polygon(pts, commonProps(shapeSettings, color));
    // Center after creation so Fabric has computed the bounding box
    poly.set({ left: cx - (poly.width || 0) / 2, top: cy - (poly.height || 0) / 2 });
    maybeApplyStroke(poly, shapeSettings);
    return poly;
  };

  const createStar = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const outer = 50, inner = 25, n = 5;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < n * 2; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (i * Math.PI) / n - Math.PI / 2;
      pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createOval = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Ellipse => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    const oval = new fabric.Ellipse({ left: cx - 60, top: cy - 40, rx: 60, ry: 40, ...commonProps(shapeSettings, color) });
    maybeApplyStroke(oval, shapeSettings);
    return oval;
  };

  const createTriangle = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Triangle => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    const tri = new fabric.Triangle({ left: cx - 50, top: cy - 50, width: 100, height: 100, ...commonProps(shapeSettings, color) });
    maybeApplyStroke(tri, shapeSettings);
    return tri;
  };

  const createHexagon = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const r = 50;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < 6; i++) {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createArrow = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const pts = [
      { x: -50, y: -15 }, { x: 15, y: -15 },
      { x: 15, y: -30 }, { x: 50, y: 0 },
      { x: 15, y: 30 }, { x: 15, y: 15 },
      { x: -50, y: 15 },
    ];
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createChatBubble = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Path => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    // Path uses positive coordinates; Fabric calculates bounding box automatically
    const path = new fabric.Path(
      'M 5 0 Q 0 0 0 5 L 0 40 Q 0 45 5 45 L 30 45 L 40 55 L 50 45 L 75 45 Q 80 45 80 40 L 80 5 Q 80 0 75 0 Z',
      { ...commonProps(shapeSettings, color) }
    );
    // Center after creation so Fabric has computed the bounding box
    path.set({ left: cx - (path.width || 80) / 2, top: cy - (path.height || 55) / 2 });
    maybeApplyStroke(path, shapeSettings);
    return path;
  };

  const createHeart = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Path => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    // Path uses positive coordinates starting from top-left
    const path = new fabric.Path(
      'M 40 15 C 40 5 30 -5 20 -5 C 5 -5 0 10 0 15 C 0 40 40 65 40 65 C 40 65 80 40 80 15 C 80 10 75 -5 60 -5 C 50 -5 40 5 40 15 Z',
      { ...commonProps(shapeSettings, color) }
    );
    path.set({ left: cx - (path.width || 80) / 2, top: cy - (path.height || 70) / 2 });
    maybeApplyStroke(path, shapeSettings);
    return path;
  };

  const createDiamond = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const pts = [
      { x: 0, y: -55 }, { x: 40, y: 0 },
      { x: 0, y: 55 }, { x: -40, y: 0 },
    ];
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createPentagon = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const r = 50;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < 5; i++) {
      const a = (2 * Math.PI / 5) * i - Math.PI / 2;
      pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createOctagon = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const r = 50;
    const pts: { x: number; y: number }[] = [];
    for (let i = 0; i < 8; i++) {
      const a = (Math.PI / 4) * i - Math.PI / 8;
      pts.push({ x: r * Math.cos(a), y: r * Math.sin(a) });
    }
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createCross = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Polygon => {
    const s = 50, t = 18;
    const pts = [
      { x: -t, y: -s }, { x: t, y: -s },
      { x: t, y: -t }, { x: s, y: -t },
      { x: s, y: t }, { x: t, y: t },
      { x: t, y: s }, { x: -t, y: s },
      { x: -t, y: t }, { x: -s, y: t },
      { x: -s, y: -t }, { x: -t, y: -t },
    ];
    return createCenteredPolygon(canvas, shapeSettings, color, pts);
  };

  const createRoundedRect = (canvas: fabric.Canvas, shapeSettings: any, color: string): fabric.Rect => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    const rect = new fabric.Rect({ left: cx - 60, top: cy - 40, width: 120, height: 80, rx: 16, ry: 16, ...commonProps(shapeSettings, color) });
    maybeApplyStroke(rect, shapeSettings);
    return rect;
  };

  const createRing = (canvas: fabric.Canvas, _shapeSettings: any, color: string): fabric.Circle => {
    const cx = canvas.width! / 2, cy = canvas.height! / 2;
    const ring = new fabric.Circle({
      left: cx - 50, top: cy - 50, radius: 50,
      fill: 'transparent',
      stroke: color,
      strokeWidth: 12,
      selectable: true, hasControls: true, hasBorders: true,
      cornerStyle: 'circle', cornerSize: 8, transparentCorners: false,
      cornerColor: '#4285f4', cornerStrokeColor: '#ffffff',
      borderColor: '#4285f4', borderScaleFactor: 1, padding: 8,
      strokeUniform: true,
    });
    return ring;
  };

  const shapes: ShapeDefinition[] = [
    {
      id: 'star', label: 'Star', color: '#f59e0b',
      icon: <svg viewBox="0 0 100 100"><polygon points="50,8 61,38 93,38 67,58 78,88 50,70 22,88 33,58 7,38 39,38" fill="#f59e0b"/></svg>,
      createShape: createStar,
    },
    {
      id: 'triangle', label: 'Triangle', color: '#10b981',
      icon: <svg viewBox="0 0 100 100"><polygon points="50,12 90,88 10,88" fill="#10b981"/></svg>,
      createShape: createTriangle,
    },
    {
      id: 'oval', label: 'Oval', color: '#8b5cf6',
      icon: <svg viewBox="0 0 100 100"><ellipse cx="50" cy="50" rx="42" ry="28" fill="#8b5cf6"/></svg>,
      createShape: createOval,
    },
    {
      id: 'hexagon', label: 'Hexagon', color: '#3b82f6',
      icon: <svg viewBox="0 0 100 100"><polygon points="50,8 90,28 90,72 50,92 10,72 10,28" fill="#3b82f6"/></svg>,
      createShape: createHexagon,
    },
    {
      id: 'pentagon', label: 'Pentagon', color: '#06b6d4',
      icon: <svg viewBox="0 0 100 100"><polygon points="50,8 95,38 77,88 23,88 5,38" fill="#06b6d4"/></svg>,
      createShape: createPentagon,
    },
    {
      id: 'diamond', label: 'Diamond', color: '#ec4899',
      icon: <svg viewBox="0 0 100 100"><polygon points="50,8 88,50 50,92 12,50" fill="#ec4899"/></svg>,
      createShape: createDiamond,
    },
    {
      id: 'arrow', label: 'Arrow', color: '#ef4444',
      icon: <svg viewBox="0 0 100 100"><polygon points="8,40 58,40 58,18 92,50 58,82 58,60 8,60" fill="#ef4444"/></svg>,
      createShape: createArrow,
    },
    {
      id: 'heart', label: 'Heart', color: '#f43f5e',
      icon: <svg viewBox="0 0 100 100"><path d="M 50 30 C 50 20 40 10 30 10 C 15 10 10 25 10 30 C 10 55 50 80 50 80 C 50 80 90 55 90 30 C 90 25 85 10 70 10 C 60 10 50 20 50 30 Z" fill="#f43f5e"/></svg>,
      createShape: createHeart,
    },
    {
      id: 'chat-bubble', label: 'Callout', color: '#6366f1',
      icon: <svg viewBox="0 0 100 100"><path d="M 18 15 Q 12 15 12 22 L 12 58 Q 12 65 18 65 L 38 65 L 50 80 L 62 65 L 82 65 Q 88 65 88 58 L 88 22 Q 88 15 82 15 Z" fill="#6366f1"/></svg>,
      createShape: createChatBubble,
    },
    {
      id: 'octagon', label: 'Octagon', color: '#14b8a6',
      icon: <svg viewBox="0 0 100 100"><polygon points="35,8 65,8 92,35 92,65 65,92 35,92 8,65 8,35" fill="#14b8a6"/></svg>,
      createShape: createOctagon,
    },
    {
      id: 'cross', label: 'Cross', color: '#a855f7',
      icon: <svg viewBox="0 0 100 100"><polygon points="38,8 62,8 62,38 92,38 92,62 62,62 62,92 38,92 38,62 8,62 8,38 38,38" fill="#a855f7"/></svg>,
      createShape: createCross,
    },
    {
      id: 'rounded-rect', label: 'Rounded', color: '#f97316',
      icon: <svg viewBox="0 0 100 100"><rect x="10" y="22" width="80" height="56" rx="14" fill="#f97316"/></svg>,
      createShape: createRoundedRect,
    },
    {
      id: 'ring', label: 'Ring', color: '#0ea5e9',
      icon: <svg viewBox="0 0 100 100"><circle cx="50" cy="50" r="36" fill="none" stroke="#0ea5e9" strokeWidth="10"/></svg>,
      createShape: createRing,
    },
  ];

  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const pickerW = 280, pickerH = 380;
    let left = rect.left + rect.width + 8;
    if (left + pickerW > window.innerWidth) left = rect.left - pickerW - 8;
    let top = rect.top + rect.height / 2 - pickerH / 2;
    top = Math.max(10, Math.min(top, window.innerHeight - pickerH - 10));
    setPickerPosition({ top, left });
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        pickerRef.current && !pickerRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose, triggerRef]);

  const handleShapeSelect = (shape: ShapeDefinition) => {
    if (!canvas) return;
    const obj = shape.createShape(canvas, shapeSettings, shape.color);
    canvas.add(obj);
    canvas.setActiveObject(obj);
    setSelectedObject(obj);
    canvas.renderAll();
    addToHistory();
    setSelectedTool('select');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      ref={pickerRef}
      className="fixed z-[1000] w-[280px] rounded-2xl border border-gray-200/80 bg-white/95 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/95 dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]"
      style={{ top: pickerPosition.top, left: pickerPosition.left }}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="material-symbols-rounded text-[18px] text-gray-400 dark:text-gray-500">category</span>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Shapes</h3>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {shapes.map((shape) => (
          <button
            key={shape.id}
            onClick={() => handleShapeSelect(shape)}
            title={shape.label}
            className="group flex flex-col items-center gap-1.5 rounded-xl p-2 transition-all hover:bg-gray-100 hover:shadow-sm active:scale-95 dark:hover:bg-white/10"
          >
            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gray-50 p-2 transition-all group-hover:scale-110 group-hover:bg-white group-hover:shadow-md dark:bg-gray-800 dark:group-hover:bg-gray-700">
              <svg viewBox="0 0 100 100" className="h-full w-full">
                {(shape.icon as React.ReactElement).props.children}
              </svg>
            </div>
            <span className="text-[10px] font-medium leading-tight text-gray-500 dark:text-gray-400">
              {shape.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ShapePicker;
